import { LOGIN_TITLE, LOGIN_SUBTITLE } from '@/config/brandConfig'

/**
 * Login Screen Configuration
 * Easy to customize - change colors, text, or button styles!
 *
 * WIREFRAME DESIGN - Feel free to modify to match your brand
 * All visual elements are configurable here for easy future changes
 */

/**
 * Color Scheme - Tailwind classes
 * Change these to match your desired theme
 */
export const loginColors = {
  // Background - starry night gradient (applied via CSS class)
  background: 'starry-background',

  // Container box - frosted glass white
  containerBackground: 'bg-white/95 backdrop-blur-sm',
  containerShadow: 'shadow-xl',
  containerRounded: 'rounded-2xl',

  // Join button - blue gradient
  joinButtonBackground: 'bg-gradient-to-r from-[#3b5998] to-[#5b7fc7]',
  joinButtonHover: 'hover:from-[#2d4373] hover:to-[#4a6fa5]',
  joinButtonText: 'text-white',

  // Login button - blue gradient
  loginButtonBackground: 'bg-gradient-to-r from-[#3b5998] to-[#5b7fc7]',
  loginButtonHover: 'hover:from-[#2d4373] hover:to-[#4a6fa5]',
  loginButtonText: 'text-white',

  // Submit button (email form) - blue gradient
  submitButtonBackground: 'bg-gradient-to-r from-[#3b5998] to-[#5b7fc7]',
  submitButtonHover: 'hover:from-[#2d4373] hover:to-[#4a6fa5]',
  submitButtonText: 'text-white',

  // Header text (white to be visible on starry background)
  titleText: 'text-white',
  subtitleText: 'text-white/70',

  // Footer/Terms text
  termsText: 'text-[#4a4a6a]',

  // Input fields
  inputBorder: 'border-gray-300',
  inputFocus: 'focus:ring-[#4a6fa5] focus:border-[#4a6fa5]',

  // Error text
  errorText: 'text-red-600',

  // Back button
  backButtonText: 'text-[#4a4a6a]',
  backButtonHover: 'hover:text-[#1e2761]'
}

/**
 * UI Text
 * All text used in the login screen
 */
export const loginText = {
  // Header
  title: LOGIN_TITLE,
  subtitle: LOGIN_SUBTITLE,

  // Buttons (initial state)
  loginButton: 'enter',

  // Coming soon message for join
  comingSoonMessage: 'Coming Soon: Request to join will be available soon!',

  // Email form (after clicking login)
  emailLabel: 'Email address',
  emailPlaceholder: 'you@example.com',
  submitButton: 'Send login code',
  submitButtonLoading: 'Sending code...',
  backButton: '← Back',

  // Terms
  termsText: 'by logging in you agree to the terms of service and privacy policy',

  // Validation
  emailValidationError: 'Please enter a valid email address'
}

/**
 * Layout Configuration
 * Defines spacing, sizing, and arrangement
 */
export const loginLayout = {
  // Container sizing
  maxWidth: 'max-w-md',
  padding: 'px-4',

  // Spacing
  headerMarginBottom: 'mb-6',
  containerPadding: 'p-8', // Increased from p-6 to make box taller
  buttonSpacing: 'space-y-4',
  formSpacing: 'space-y-4',

  // Button sizing
  buttonPadding: 'py-3',
  buttonRounded: 'rounded-lg',
  buttonFontSize: 'text-sm',

  // Animations
  enableAnimations: true,
  animationDuration: 'animate-slide-up',
  animationDelay: 'animate-delay-100',
  fadeInAnimation: 'animate-fade-in'
}

/**
 * Feature Flags
 * Enable/disable features easily
 */
export const loginFeatures = {
  // Show terms of service text
  showTerms: true,

  // Show animations
  enableAnimations: true,

  // Enable button hover effects
  enableHoverEffects: true,

  // Show subtitle
  showSubtitle: false,

  // Join button behavior
  joinButtonBehavior: 'alert' as 'alert' | 'navigate' // 'alert' or 'navigate' to /join route
}

/**
 * Button Configuration
 */
export const loginButtons = {
  login: {
    label: loginText.loginButton,
    type: 'button' as const,
    fontWeight: 'font-bold'
  },
  submit: {
    label: loginText.submitButton,
    loadingLabel: loginText.submitButtonLoading,
    type: 'submit' as const,
    fontWeight: 'font-bold'
  }
}
